import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MessageCircle, Upload, FileText, Mic, Music, DollarSign, Clock, Users, Sparkles, Loader2, CheckCircle2, AlertCircle, X, Save, Copy, FileArchive } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config';
import { toDisplayText } from '../utils/safeRenderText';
import { mapWhatsAppAnalysisToExtraction } from '../utils/whatsappToExtraction';
import ClientTextImportDialog from './ClientTextImportDialog';

const API = API_URL;

const WhatsAppImporter = ({ eventId, eventName, onAnalysisComplete, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('paste');
  const [chatText, setChatText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ messages: 0, audios: 0 });
  // Revisión ítem-por-ítem (igual que Texto/Documentos) antes de aplicar al evento.
  const [showReview, setShowReview] = useState(false);
  const [reviewExtraction, setReviewExtraction] = useState(null);

  const resetState = () => {
    setChatText('');
    setSelectedFile(null);
    setAnalysis(null);
    setError(null);
    setStats({ messages: 0, audios: 0 });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ['.txt', '.zip'];
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!validExtensions.includes(ext)) {
        toast.error('Formato no soportado. Usa .txt o .zip');
        return;
      }
      
      // Mostrar advertencia si el archivo es muy grande
      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > 100) {
        toast.info(`Archivo grande (${sizeMB.toFixed(1)}MB). Se subirá en partes...`);
      }
      
      setSelectedFile(file);
      setError(null);
    }
  };

  // Chunked upload para archivos grandes (> 800KB)
  const CHUNK_SIZE = 800 * 1024; // 800KB por chunk (debajo del límite de 1MB del proxy)
  
  const uploadChunked = async (file, eventId, token) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    
    // 1. Inicializar upload
    const initResponse = await axios.post(
      `${API}/whatsapp/upload/init`,
      {
        filename: file.name,
        total_size: file.size,
        total_chunks: totalChunks
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    const uploadId = initResponse.data.upload_id;
    
    try {
      // 2. Subir chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        
        const chunkFormData = new FormData();
        chunkFormData.append('file', chunk, `chunk_${i}`);
        
        await axios.post(
          `${API}/whatsapp/upload/chunk/${uploadId}/${i}`,
          chunkFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        // Actualizar progreso
        const progress = Math.round(((i + 1) / totalChunks) * 100);
        toast.loading(`Subiendo ${sizeMB}MB... ${progress}%`, { id: 'upload-progress' });
      }
      
      toast.dismiss('upload-progress');
      toast.loading('Procesando archivo...', { id: 'processing' });
      
      // 3. Completar y procesar
      const completeResponse = await axios.post(
        `${API}/whatsapp/upload/complete/${uploadId}`,
        null,
        {
          params: { event_id: eventId || undefined },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.dismiss('processing');
      return completeResponse.data;
      
    } catch (err) {
      // Cancelar upload en caso de error
      try {
        await axios.delete(`${API}/whatsapp/upload/${uploadId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (cancelErr) {
        console.warn('[WhatsAppImporter] No se pudo cancelar upload tras fallo:', cancelErr?.message);
      }
      throw err;
    }
  };

  const analyzeText = async () => {
    if (!chatText.trim()) {
      toast.error('Pega el contenido del chat de WhatsApp');
      return;
    }
    
    setAnalyzing(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/whatsapp/analyze-text`,
        {
          chat_text: chatText,
          event_id: eventId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setAnalysis(response.data.analysis);
        setStats({
          messages: response.data.messages_count,
          audios: response.data.audios_count
        });
        toast.success('Chat analizado correctamente');
      } else {
        setError(response.data.error || 'Error al analizar');
        toast.error(response.data.error || 'Error al analizar');
      }
    } catch (err) {
      console.error('Error analyzing text:', err);
      setError(err.response?.data?.detail || 'Error al analizar el chat');
      toast.error('Error al analizar el chat');
    } finally {
      setAnalyzing(false);
    }
  };

  const uploadAndAnalyze = async () => {
    if (!selectedFile) {
      toast.error('Selecciona un archivo');
      return;
    }
    
    setAnalyzing(true);
    setError(null);
    
    const sizeMB = selectedFile.size / (1024 * 1024);
    const sizeKB = selectedFile.size / 1024;
    console.log(`[WhatsApp] File selected: ${selectedFile.name}, size: ${sizeMB.toFixed(2)}MB (${sizeKB.toFixed(0)}KB)`);
    
    try {
      const token = localStorage.getItem('token');
      
      let response;
      
      // SIEMPRE usar chunked upload para archivos ZIP (tienen multimedia)
      // Los ZIPs de WhatsApp con multimedia suelen ser grandes
      const isZip = selectedFile.name.toLowerCase().endsWith('.zip');
      const useChunked = isZip || selectedFile.size > 500 * 1024; // 500KB para ser conservador
      
      if (useChunked) {
        console.log(`[WhatsApp] Using CHUNKED upload for ${sizeMB.toFixed(1)}MB file (isZip: ${isZip})`);
        toast.loading(`Preparando subida de ${sizeMB.toFixed(1)}MB...`, { id: 'upload-prep' });
        
        try {
          const result = await uploadChunked(selectedFile, eventId, token);
          response = { data: result };
          toast.dismiss('upload-prep');
        } catch (chunkErr) {
          toast.dismiss('upload-prep');
          console.error('[WhatsApp] Chunked upload failed:', chunkErr);
          throw chunkErr;
        }
      } else {
        // Upload normal solo para archivos TXT pequeños
        console.log(`[WhatsApp] Using DIRECT upload for small ${sizeKB.toFixed(0)}KB file`);
        const formData = new FormData();
        formData.append('file', selectedFile);
        if (eventId) {
          formData.append('event_id', eventId);
        }
        
        response = await axios.post(
          `${API}/whatsapp/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }
      
      if (response.data.success) {
        setAnalysis(response.data.analysis);
        setStats({
          messages: response.data.messages_count,
          audios: response.data.audios_count
        });
        toast.success(`Analizado: ${response.data.messages_count} mensajes, ${response.data.audios_count} audios`);
      } else {
        setError(response.data.error || 'Error al analizar');
        toast.error(response.data.error || 'Error al analizar');
      }
    } catch (err) {
      console.error('[WhatsApp] Error uploading file:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Error al procesar el archivo';
      
      // Mensaje específico para error 413
      if (err.response?.status === 413) {
        setError(`El archivo (${sizeMB.toFixed(1)}MB) es demasiado grande para subir directamente. Esto no debería pasar con chunked upload. Por favor, reporta este error.`);
        toast.error('Error inesperado: archivo demasiado grande');
      } else {
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  // En vez de volcar todo el análisis de golpe (todo-o-nada), se mapea al
  // mismo esquema que usa el flujo de Texto y se abre la misma revisión
  // ítem-por-ítem (checkbox + edición de canciones, momentos, notas, tareas
  // y horarios) antes de aplicar nada al evento.
  const openReview = () => {
    if (!analysis || !eventId) return;
    setReviewExtraction(mapWhatsAppAnalysisToExtraction(analysis));
    setShowReview(true);
  };

  const renderAnalysisResults = () => {
    if (!analysis) return null;
    
    return (
      <ScrollArea className="h-[400px] pr-4">
        <div className="space-y-4">
          {/* Resumen Detallado */}
          {(analysis.resumen_detallado || analysis.resumen) && (
            <Card className="p-4 bg-gradient-to-r from-purple-900/30 to-blue-900/30 border-purple-700/50">
              <h4 className="text-sm font-semibold text-purple-300 mb-2 flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Resumen Detallado
              </h4>
              <p className="text-sm text-slate-300 whitespace-pre-line">
                {analysis.resumen_detallado || analysis.resumen}
              </p>
            </Card>
          )}

          {/* Transcripciones de Audios */}
          {analysis.transcripciones_audios?.length > 0 && (
            <Card className="p-4 bg-amber-900/20 border-amber-700/50">
              <h4 className="text-sm font-semibold text-amber-400 mb-2 flex items-center gap-2">
                <Mic className="h-4 w-4" />
                Contenido de Audios
              </h4>
              <ul className="space-y-2">
                {analysis.transcripciones_audios.map((t, idx) => (
                  <li key={`transcripcion-${idx}`} className="text-sm text-slate-300 flex items-start gap-2">
                    <span className="text-amber-500 shrink-0">🎤</span>
                    {toDisplayText(t)}
                  </li>
                ))}
              </ul>
            </Card>
          )}
          
          {/* Información económica */}
          {(analysis.precio_acordado || analysis.senal_anticipo) && (
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-semibold text-green-400 mb-2 flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Económico
              </h4>
              {analysis.precio_acordado && (
                <p className="text-white font-medium">{analysis.precio_acordado}</p>
              )}
              {analysis.forma_pago && (
                <p className="text-sm text-slate-400 mt-1">Forma de pago: {analysis.forma_pago}</p>
              )}
              {analysis.senal_anticipo && (
                <p className="text-sm text-slate-400 mt-1">Señal/Anticipo: {analysis.senal_anticipo}</p>
              )}
            </Card>
          )}
          
          {/* Canciones - Versión mejorada */}
          {(analysis.cancion_primer_baile || analysis.cancion_entrada_novia || analysis.cancion_entrada_novio || 
            analysis.cancion_entrada_conjunta || analysis.cancion_tarta || analysis.cancion_padre_hija || 
            analysis.cancion_madre_hijo || analysis.canciones_solicitadas?.length > 0) && (
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-semibold text-blue-400 mb-3 flex items-center gap-2">
                <Music className="h-4 w-4" />
                Canciones
              </h4>
              <div className="space-y-2">
                {analysis.cancion_primer_baile && (
                  <div className="flex items-start gap-2">
                    <Badge className="bg-pink-600 text-xs shrink-0">Primer baile</Badge>
                    <span className="text-sm text-slate-300">
                      {analysis.cancion_primer_baile.titulo} - {analysis.cancion_primer_baile.artista}
                    </span>
                  </div>
                )}
                {analysis.cancion_entrada_novia && (
                  <div className="flex items-start gap-2">
                    <Badge className="bg-rose-600 text-xs shrink-0">Entrada Novia</Badge>
                    <span className="text-sm text-slate-300">
                      {analysis.cancion_entrada_novia.titulo} - {analysis.cancion_entrada_novia.artista}
                      {analysis.cancion_entrada_novia.momento && (
                        <span className="text-slate-500 text-xs ml-1">({analysis.cancion_entrada_novia.momento})</span>
                      )}
                    </span>
                  </div>
                )}
                {analysis.cancion_entrada_novio && (
                  <div className="flex items-start gap-2">
                    <Badge className="bg-blue-600 text-xs shrink-0">Entrada Novio</Badge>
                    <span className="text-sm text-slate-300">
                      {analysis.cancion_entrada_novio.titulo} - {analysis.cancion_entrada_novio.artista}
                      {analysis.cancion_entrada_novio.momento && (
                        <span className="text-slate-500 text-xs ml-1">({analysis.cancion_entrada_novio.momento})</span>
                      )}
                    </span>
                  </div>
                )}
                {analysis.cancion_entrada_conjunta && (
                  <div className="flex items-start gap-2">
                    <Badge className="bg-amber-600 text-xs shrink-0">Entrada Conjunta</Badge>
                    <span className="text-sm text-slate-300">
                      {analysis.cancion_entrada_conjunta.titulo} - {analysis.cancion_entrada_conjunta.artista}
                    </span>
                  </div>
                )}
                {analysis.cancion_tarta && (
                  <div className="flex items-start gap-2">
                    <Badge className="bg-purple-600 text-xs shrink-0">Tarta</Badge>
                    <span className="text-sm text-slate-300">
                      {analysis.cancion_tarta.titulo} - {analysis.cancion_tarta.artista}
                    </span>
                  </div>
                )}
                {analysis.cancion_padre_hija && (
                  <div className="flex items-start gap-2">
                    <Badge className="bg-cyan-600 text-xs shrink-0">Padre-Hija</Badge>
                    <span className="text-sm text-slate-300">
                      {analysis.cancion_padre_hija.titulo} - {analysis.cancion_padre_hija.artista}
                    </span>
                  </div>
                )}
                {analysis.cancion_madre_hijo && (
                  <div className="flex items-start gap-2">
                    <Badge className="bg-teal-600 text-xs shrink-0">Madre-Hijo</Badge>
                    <span className="text-sm text-slate-300">
                      {analysis.cancion_madre_hijo.titulo} - {analysis.cancion_madre_hijo.artista}
                    </span>
                  </div>
                )}
                {analysis.canciones_solicitadas?.length > 0 && (
                  <div className="mt-3 pt-2 border-t border-slate-700">
                    <p className="text-xs text-slate-500 mb-2">Otras canciones solicitadas:</p>
                    <div className="space-y-1">
                      {analysis.canciones_solicitadas.map((song, idx) => (
                        <div key={`song-${idx}-${typeof song === 'string' ? song.slice(0,10) : song.titulo}`} className="flex items-center gap-2 text-sm text-slate-300">
                          <span className="text-blue-400">♪</span>
                          {typeof song === 'string' ? song : (
                            <>
                              {song.titulo}{song.artista && ` - ${song.artista}`}
                              {song.momento && <span className="text-slate-500 text-xs ml-1">({song.momento})</span>}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Momentos Especiales */}
          {analysis.momentos_especiales?.length > 0 && (
            <Card className="p-4 bg-gradient-to-r from-amber-900/20 to-orange-900/20 border-amber-700/50">
              <h4 className="text-sm font-semibold text-amber-400 mb-2 flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Momentos Especiales
              </h4>
              <div className="space-y-3">
                {analysis.momentos_especiales.map((m, idx) => (
                  <div key={`momento-esp-${idx}-${m.descripcion?.slice(0,10) || idx}`} className="bg-slate-800/50 rounded p-2">
                    <p className="text-sm text-white font-medium">{m.descripcion}</p>
                    {m.cuando && <p className="text-xs text-slate-400">⏰ {m.cuando}</p>}
                    {m.participantes && <p className="text-xs text-slate-400">👥 {m.participantes}</p>}
                    {m.notas && <p className="text-xs text-slate-500 mt-1">{m.notas}</p>}
                  </div>
                ))}
              </div>
            </Card>
          )}
          
          {/* Géneros/canciones vetados */}
          {(analysis.generos_vetados?.length > 0 || analysis.canciones_vetadas?.length > 0) && (
            <Card className="p-4 bg-red-900/20 border-red-700/50">
              <h4 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-2">
                <X className="h-4 w-4" />
                NO poner
              </h4>
              <div className="flex flex-wrap gap-1">
                {analysis.generos_vetados?.map((g, idx) => (
                  <Badge key={`g-${idx}`} className="bg-red-800 text-xs">{g}</Badge>
                ))}
                {analysis.canciones_vetadas?.map((c, idx) => (
                  <Badge key={`c-${idx}`} variant="outline" className="border-red-700 text-red-300 text-xs">{c}</Badge>
                ))}
              </div>
            </Card>
          )}

          {/* Géneros preferidos */}
          {analysis.generos_preferidos?.length > 0 && (
            <Card className="p-4 bg-green-900/20 border-green-700/50">
              <h4 className="text-sm font-semibold text-green-400 mb-2">Géneros Preferidos</h4>
              <div className="flex flex-wrap gap-1">
                {analysis.generos_preferidos.map((g, idx) => (
                  <Badge key={`genero-pref-${idx}-${g}`} className="bg-green-800 text-xs">{g}</Badge>
                ))}
              </div>
            </Card>
          )}

          {/* Fotomatón - Versión mejorada */}
          {analysis.fotomaton && (analysis.fotomaton.contratado || analysis.fotomaton.tipo) && (
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-semibold text-cyan-400 mb-2">📸 Fotomatón</h4>
              <div className="space-y-1 text-sm text-slate-300">
                {analysis.fotomaton.tipo && <p><span className="text-slate-500">Tipo:</span> {analysis.fotomaton.tipo}</p>}
                {analysis.fotomaton.fondo && <p><span className="text-slate-500">Fondo:</span> {analysis.fotomaton.fondo}</p>}
                {analysis.fotomaton.props && <p><span className="text-slate-500">Props:</span> {analysis.fotomaton.props}</p>}
                {analysis.fotomaton.ubicacion && <p><span className="text-slate-500">Ubicación:</span> {analysis.fotomaton.ubicacion}</p>}
                {analysis.fotomaton.horario && <p><span className="text-slate-500">Horario:</span> {analysis.fotomaton.horario}</p>}
                {analysis.fotomaton.detalles && <p>{analysis.fotomaton.detalles}</p>}
              </div>
            </Card>
          )}
          
          {/* Horarios - Versión mejorada */}
          {analysis.horarios && (
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-semibold text-amber-400 mb-2 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Horarios
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm text-slate-300">
                {analysis.horarios.llegada_montaje && <p>🚚 Montaje: {analysis.horarios.llegada_montaje}</p>}
                {analysis.horarios.ceremonia && <p>💒 Ceremonia: {analysis.horarios.ceremonia}</p>}
                {analysis.horarios.coctel && <p>🥂 Cóctel: {analysis.horarios.coctel}</p>}
                {analysis.horarios.banquete && <p>🍽️ Banquete: {analysis.horarios.banquete}</p>}
                {analysis.horarios.primer_baile && <p>💃 Primer baile: {analysis.horarios.primer_baile}</p>}
                {analysis.horarios.tarta && <p>🎂 Tarta: {analysis.horarios.tarta}</p>}
                {analysis.horarios.inicio_baile && <p>🎉 Inicio baile: {analysis.horarios.inicio_baile}</p>}
                {analysis.horarios.fin_fiesta && <p>🌙 Fin: {analysis.horarios.fin_fiesta}</p>}
              </div>
              {analysis.horarios.otros?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-700">
                  {analysis.horarios.otros.map((h, idx) => (
                    <p key={`horario-otro-${idx}-${h.momento}`} className="text-sm text-slate-400">
                      {h.momento}: {h.hora}
                    </p>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Servicios Extra */}
          {analysis.servicios_extra?.length > 0 && (
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-semibold text-purple-400 mb-2">Servicios Contratados</h4>
              <div className="space-y-1">
                {analysis.servicios_extra.map((s, idx) => (
                  <p key={`servicio-${idx}-${typeof s === 'string' ? s.slice(0,10) : s.servicio}`} className="text-sm text-slate-300">
                    {typeof s === 'string' ? s : (
                      <><span className="text-purple-300">{s.servicio}</span>: {s.detalles}</>
                    )}
                  </p>
                ))}
              </div>
            </Card>
          )}
          
          {/* Contactos */}
          {analysis.contactos?.length > 0 && (
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-semibold text-green-400 mb-2 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Contactos
              </h4>
              <div className="space-y-1">
                {analysis.contactos.map((c, idx) => (
                  <p key={`contacto-${idx}-${c.nombre}`} className="text-sm text-slate-300">
                    <span className="text-slate-500">{c.rol}:</span> {c.nombre}
                    {c.telefono && <span className="text-slate-500"> ({c.telefono})</span>}
                    {c.email && <span className="text-slate-500"> - {c.email}</span>}
                  </p>
                ))}
              </div>
            </Card>
          )}
          
          {/* Peticiones especiales */}
          {analysis.peticiones_especiales?.length > 0 && (
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-semibold text-purple-400 mb-2">Peticiones especiales</h4>
              <ul className="space-y-1">
                {analysis.peticiones_especiales.map((p, idx) => (
                  <li key={`peticion-${idx}`} className="text-sm text-slate-300 flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    {toDisplayText(p)}
                  </li>
                ))}
              </ul>
            </Card>
          )}
          
          {/* Notas importantes */}
          {analysis.notas_importantes?.length > 0 && (
            <Card className="p-4 bg-amber-900/20 border-amber-700/50">
              <h4 className="text-sm font-semibold text-amber-400 mb-2">Notas importantes</h4>
              <ul className="space-y-1">
                {analysis.notas_importantes.map((n, idx) => (
                  <li key={`nota-imp-${idx}`} className="text-sm text-slate-300">{toDisplayText(n)}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </ScrollArea>
    );
  };

  return (
    <>
    {/* Se cierra el Dialog de Radix (y su pointer-events/focus-trap) mientras
        se muestra la revisión, para que esta no quede bloqueada debajo. El
        componente sigue montado, así que el estado (analysis, reviewExtraction)
        se conserva. */}
    <Dialog open={isOpen && !showReview} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] bg-slate-900 border-slate-700 text-white overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-green-500" />
            Importar Chat de WhatsApp
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {eventName ? `Evento: ${eventName}` : 'Analiza conversaciones para extraer información del evento'}
          </DialogDescription>
        </DialogHeader>

        {!analysis ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
            <TabsList className="grid grid-cols-2 bg-slate-800">
              <TabsTrigger value="paste" className="data-[state=active]:bg-green-600">
                <Copy className="h-4 w-4 mr-2" />
                Pegar texto
              </TabsTrigger>
              <TabsTrigger value="upload" className="data-[state=active]:bg-green-600">
                <Upload className="h-4 w-4 mr-2" />
                Subir archivo
              </TabsTrigger>
            </TabsList>

            <TabsContent value="paste" className="flex-1 space-y-4 mt-4">
              <div className="bg-slate-800/50 rounded-lg p-3 text-xs text-slate-400 space-y-1">
                <p><strong>Cómo copiar el chat:</strong></p>
                <p>1. Abre WhatsApp → Chat del cliente</p>
                <p>2. Selecciona los mensajes importantes</p>
                <p>3. Copia y pega aquí</p>
              </div>
              
              <Textarea
                value={chatText}
                onChange={(e) => setChatText(e.target.value)}
                placeholder="Pega aquí los mensajes del chat de WhatsApp..."
                className="min-h-[200px] bg-slate-800 border-slate-700 text-white"
              />
              
              {error && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              )}
              
              <Button
                onClick={analyzeText}
                disabled={analyzing || !chatText.trim()}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Analizando...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Analizar con IA
                  </>
                )}
              </Button>
            </TabsContent>

            <TabsContent value="upload" className="flex-1 space-y-4 mt-4">
              <div className="bg-slate-800/50 rounded-lg p-3 text-xs text-slate-400 space-y-1">
                <p><strong>Cómo exportar el chat:</strong></p>
                <p>1. WhatsApp → Chat → Menú (⋮) → Más → Exportar chat</p>
                <p>2. Elige "Incluir multimedia" para transcribir audios</p>
                <p>3. Se genera un archivo .zip o .txt</p>
              </div>
              
              <div className="border-2 border-dashed border-slate-700 rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept=".txt,.zip"
                  onChange={handleFileChange}
                  className="hidden"
                  id="whatsapp-file-input"
                />
                <label
                  htmlFor="whatsapp-file-input"
                  className="cursor-pointer flex flex-col items-center gap-3"
                >
                  {selectedFile ? (
                    <>
                      {selectedFile.name.endsWith('.zip') ? (
                        <FileArchive className="h-12 w-12 text-amber-500" />
                      ) : (
                        <FileText className="h-12 w-12 text-green-500" />
                      )}
                      <div>
                        <p className="text-white font-medium">{selectedFile.name}</p>
                        <p className="text-xs text-slate-500">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                          {selectedFile.name.endsWith('.zip') && ' (con multimedia)'}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <Upload className="h-12 w-12 text-slate-500" />
                      <div>
                        <p className="text-slate-300">Arrastra o haz clic para seleccionar</p>
                        <p className="text-xs text-slate-500">.txt (solo texto) o .zip (con audios)</p>
                      </div>
                    </>
                  )}
                </label>
              </div>
              
              {selectedFile?.name.endsWith('.zip') && (
                <div className="flex items-center gap-2 text-amber-400 text-sm bg-amber-900/20 p-2 rounded">
                  <Mic className="h-4 w-4" />
                  Los audios serán transcritos automáticamente
                </div>
              )}
              
              {error && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              )}
              
              <Button
                onClick={uploadAndAnalyze}
                disabled={analyzing || !selectedFile}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {selectedFile?.name.endsWith('.zip') ? 'Transcribiendo audios y analizando...' : 'Analizando...'}
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Analizar con IA
                  </>
                )}
              </Button>
            </TabsContent>
          </Tabs>
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Stats */}
            <div className="flex items-center gap-3 mb-4">
              <Badge className="bg-green-600">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Analizado
              </Badge>
              <Badge variant="outline" className="border-slate-600">
                {stats.messages} mensajes
              </Badge>
              {stats.audios > 0 && (
                <Badge variant="outline" className="border-amber-600 text-amber-400">
                  <Mic className="h-3 w-3 mr-1" />
                  {stats.audios} audios transcritos
                </Badge>
              )}
            </div>
            
            {/* Results */}
            {renderAnalysisResults()}
            
            {/* Actions */}
            <div className="flex gap-2 mt-4 pt-4 border-t border-slate-700">
              <Button
                variant="outline"
                onClick={resetState}
                className="border-slate-700"
              >
                Analizar otro
              </Button>
              <Button
                onClick={openReview}
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={!eventId}
                data-testid="whatsapp-review-apply-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                Revisar y aplicar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    <ClientTextImportDialog
      open={showReview}
      onClose={() => setShowReview(false)}
      eventId={eventId}
      eventName={eventName}
      externalExtraction={reviewExtraction}
      title="Revisar análisis de WhatsApp"
      source="whatsapp"
      onApplied={() => {
        setShowReview(false);
        onAnalysisComplete?.(analysis);
        onClose();
      }}
    />
    </>
  );
};

export default WhatsAppImporter;
